import { useEffect, useState } from "react";
import { gymClient, ApiError } from "../../apiClient/gymClient";
import { addDays, formatFullDate, slotStartTimes, startOfToday, toISODate } from "../../utils/dates";
import { GYM_RULES } from "../../types/gym.types";
import type { DayOverride, SlotOverride } from "../../types/gym.types";

const LIST_WINDOW_DAYS = 30;

function parseCapacityInput(raw: string, setError: (msg: string) => void): { ok: true; value: number | null } | { ok: false } {
  if (raw.trim() === "") return { ok: true, value: null };
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 0) {
    setError("Capacity must be a non-negative whole number, or left blank.");
    return { ok: false };
  }
  return { ok: true, value: n };
}

export function AdminDayOverridesTab() {
  return (
    <div>
      <DayOverridesSection />
      <SlotOverridesSection />
    </div>
  );
}

function DayOverridesSection() {
  const [overrides, setOverrides] = useState<DayOverride[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [date, setDate] = useState(() => toISODate(startOfToday()));
  const [isClosed, setIsClosed] = useState(false);
  const [capacityOverride, setCapacityOverride] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function refresh() {
    setIsLoading(true);
    setError(null);
    const from = toISODate(startOfToday());
    const to = toISODate(addDays(startOfToday(), LIST_WINDOW_DAYS));
    gymClient
      .getDayOverrides(from, to)
      .then((rows) => setOverrides(rows.sort((a, b) => a.date.localeCompare(b.date))))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load overrides."))
      .finally(() => setIsLoading(false));
  }

  useEffect(refresh, []);

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const capacity = parseCapacityInput(capacityOverride, setSaveError);
      if (!capacity.ok) {
        setSaving(false);
        return;
      }
      const patch: { isClosed?: boolean; capacityOverride?: number | null; note?: string } = {
        isClosed,
        capacityOverride: capacity.value,
      };
      if (note.trim()) patch.note = note.trim();

      await gymClient.setDayOverride(date, patch);
      setNote("");
      refresh();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Couldn't save this override.");
    } finally {
      setSaving(false);
    }
  }

  async function handleClear(overrideDate: string) {
    try {
      await gymClient.clearDayOverride(overrideDate);
      refresh();
    } catch {
      // refresh() will surface a stale-but-visible list either way; no need for a modal here.
    }
  }

  return (
    <div>
      <div className="form-card">
        <h2>Close a whole day</h2>
        <div className="form">
          <div className="form-field">
            <label htmlFor="override-date">Date</label>
            <input id="override-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>

          <label className="radio">
            <input type="checkbox" checked={isClosed} onChange={(e) => setIsClosed(e.target.checked)} />
            Close the gym this day
          </label>

          <div className="form-field">
            <label htmlFor="override-capacity">Capacity override</label>
            <input
              id="override-capacity"
              type="number"
              min={0}
              placeholder={`Default: ${GYM_RULES.slotCapacity}`}
              value={capacityOverride}
              onChange={(e) => setCapacityOverride(e.target.value)}
            />
            <span className="field-hint">Leave blank to use the default capacity ({GYM_RULES.slotCapacity}).</span>
          </div>

          <div className="form-field">
            <label htmlFor="override-note">Note (optional)</label>
            <input
              id="override-note"
              type="text"
              placeholder="e.g. Public holiday, maintenance"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {saveError && <div className="form-banner form-banner--error">{saveError}</div>}

          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save override"}
            </button>
          </div>
        </div>
      </div>

      <h2 className="admin-section-heading">Upcoming day overrides (next {LIST_WINDOW_DAYS} days)</h2>
      {error ? (
        <div className="state-panel">
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : overrides.length === 0 ? (
        <div className="state-panel">
          <p>No day overrides set — every day uses the default schedule.</p>
        </div>
      ) : (
        <div className="bookings-day">
          <ul className="bookings-day-items">
            {overrides.map((o) => (
              <li className="booking-row" key={o.date}>
                <span className="attendee">
                  <span className="attendee-name">{formatFullDate(o.date)}</span>
                  <span className="attendee-mobile">
                    {o.isClosed ? "Closed" : `Capacity ${o.capacityOverride ?? GYM_RULES.slotCapacity}`}
                    {o.note ? ` — ${o.note}` : ""}
                  </span>
                </span>
                <button type="button" className="btn btn-danger-ghost" onClick={() => handleClear(o.date)}>
                  Clear
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SlotOverridesSection() {
  const times = slotStartTimes();

  const [overrides, setOverrides] = useState<SlotOverride[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [date, setDate] = useState(() => toISODate(startOfToday()));
  const [startTime, setStartTime] = useState(times[0] ?? "");
  const [isClosed, setIsClosed] = useState(true);
  const [capacityOverride, setCapacityOverride] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function refresh() {
    setIsLoading(true);
    setError(null);
    const from = toISODate(startOfToday());
    const to = toISODate(addDays(startOfToday(), LIST_WINDOW_DAYS));
    gymClient
      .getSlotOverrides(from, to)
      .then((rows) => setOverrides(rows.sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime))))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load slot overrides."))
      .finally(() => setIsLoading(false));
  }

  useEffect(refresh, []);

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const capacity = parseCapacityInput(capacityOverride, setSaveError);
      if (!capacity.ok) {
        setSaving(false);
        return;
      }
      const patch: { isClosed?: boolean; capacityOverride?: number | null; note?: string } = {
        isClosed,
        capacityOverride: capacity.value,
      };
      if (note.trim()) patch.note = note.trim();

      await gymClient.setSlotOverride(date, startTime, patch);
      setNote("");
      refresh();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Couldn't save this override.");
    } finally {
      setSaving(false);
    }
  }

  async function handleClear(overrideDate: string, overrideStartTime: string) {
    try {
      await gymClient.clearSlotOverride(overrideDate, overrideStartTime);
      refresh();
    } catch {
      // refresh() will surface a stale-but-visible list either way; no need for a modal here.
    }
  }

  return (
    <div className="admin-subsection">
      <div className="form-card">
        <h2>Close one specific slot</h2>
        <p className="field-hint">
          Leaves the rest of the day open. A day that's already closed can't be reopened by overriding a slot on it.
        </p>
        <div className="form">
          <div className="form-field">
            <label htmlFor="slot-override-date">Date</label>
            <input id="slot-override-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>

          <div className="form-field">
            <label htmlFor="slot-override-time">Time</label>
            <select id="slot-override-time" value={startTime} onChange={(e) => setStartTime(e.target.value)}>
              {times.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <label className="radio">
            <input type="checkbox" checked={isClosed} onChange={(e) => setIsClosed(e.target.checked)} />
            Close this slot
          </label>

          <div className="form-field">
            <label htmlFor="slot-override-capacity">Capacity override</label>
            <input
              id="slot-override-capacity"
              type="number"
              min={0}
              placeholder={`Default: ${GYM_RULES.slotCapacity}`}
              value={capacityOverride}
              onChange={(e) => setCapacityOverride(e.target.value)}
            />
            <span className="field-hint">Leave blank to use the day's capacity.</span>
          </div>

          <div className="form-field">
            <label htmlFor="slot-override-note">Note (optional)</label>
            <input
              id="slot-override-note"
              type="text"
              placeholder="e.g. Coach unavailable"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {saveError && <div className="form-banner form-banner--error">{saveError}</div>}

          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save override"}
            </button>
          </div>
        </div>
      </div>

      <h2 className="admin-section-heading">Upcoming slot overrides (next {LIST_WINDOW_DAYS} days)</h2>
      {error ? (
        <div className="state-panel">
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : overrides.length === 0 ? (
        <div className="state-panel">
          <p>No individual slots overridden.</p>
        </div>
      ) : (
        <div className="bookings-day">
          <ul className="bookings-day-items">
            {overrides.map((o) => (
              <li className="booking-row" key={`${o.date}_${o.startTime}`}>
                <span className="attendee">
                  <span className="attendee-name">
                    {formatFullDate(o.date)} · {o.startTime}
                  </span>
                  <span className="attendee-mobile">
                    {o.isClosed ? "Closed" : `Capacity ${o.capacityOverride}`}
                    {o.note ? ` — ${o.note}` : ""}
                  </span>
                </span>
                <button type="button" className="btn btn-danger-ghost" onClick={() => handleClear(o.date, o.startTime)}>
                  Clear
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
